import styles from "~/sass/organisms/Article/Article.module.scss";
import Head from "~/components/Meta/Head";
import ArticleAuthor from "~/components/molecules/Article/ArticleAuthor.js";
import Image from "next/legacy/image";
import Script from "next/script";

export default function Article({}) {
  const content = () => {
    return (
      <>
        <div className={styles["Article__image"]}>
          <Image
            src={
              "/static/images/articles/homeowners-resource-guide-to-gardening.jpeg"
            }
            alt={"Statue of Hemingway"}
            width={900}
            height={565}
          />
        </div>
        <h1 className={styles["Article__heading"]}>
          A Homeowner's Resource Guide to Gardening
        </h1>
        <ArticleAuthor />
        <p>
          Gardening at home can be a rewarding hobby for people of any age, and
          it's not difficult to do. Basic gardening knowledge starts with
          understanding the characteristics of your growing area and deciding on
          plants that are suitable for this environment. Then, you can prepare
          the soil, plant your seeds or seedlings, and add water and fertilizer
          as needed.
        </p>
        <p>
          Most gardens focus on either flowers or vegetables. The growing
          techniques for both are much the same, but the goals are different:
          Flower gardening is done for aesthetic reasons, while vegetable
          gardening is intended to produce food for the grower. Often, flower
          gardens will be located in the front yard to boost curb appeal, while
          vegetable gardens will be planted in the backyard. But there's nothing
          preventing you from planting both food and flowers in the same garden.
          In fact, planting flowers near your vegetables can attract pollinators
          that will help to increase your harvest.
        </p>
        <p>
          No matter what type of garden you choose to plant, you'll need to
          think about how it will be designed. If you're growing flowers, you'll
          want to create a layout that's aesthetically pleasing, considering the
          colors and heights of the flowers you're planting as well as when they
          will bloom. Vegetables are typically planted in rows, with the tallest
          ones in the back and the shortest ones in front. Consider the sun when
          planning your layout: If one part of your garden gets a lot of sun
          while another is in the shadow of your house for part of the day, that
          will influence which plants you can put where.
        </p>
        <p>
          If you have children, involve them in the process of planting and
          tending the garden. Gardening can teach children about how plants grow
          and what they need to be healthy. You can also teach them about things
          like how to attract pollinators to the garden and how to encourage
          plants to grow without chemicals. They'll also learn responsibility by
          taking care of the garden through weeding and watering. And if you
          grow vegetables with children, they may be more likely to eat the
          results, encouraging healthier eating habits.
        </p>
        <p>
          Kids can also enjoy learning about composting and helping to create
          compost. Composting is the process of turning materials like vegetable
          scraps, grass clippings, eggshells, and paper into fertilizer for
          plants. It's fairly simple to create your own compost heap, and once
          these materials break down, you'll have nutrient-rich material that
          you can add to your{" "}
          <a href="https://www.realestateagents.com/mi/garden-mi">garden</a>{" "}
          soil.
        </p>
        <p>
          Once you get involved in gardening, you might want to join a group of
          like-minded individuals. There are many gardening societies worldwide,
          each with its own focus and community. Gardening societies can connect
          you with other gardening enthusiasts and provide resources and
          information on gardening. They may also put on workshops, lectures, or
          other events.
        </p>
        <p>
          Another way to learn more about gardening is to listen to podcasts. A
          variety of individuals and organizations make podcasts that offer
          expert information about growing techniques and advice for novices.
          Some of these are sponsored by gardening magazines, which can be
          another great resource for people who want to learn more about how to
          improve their gardening skills.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          General Gardening Advice
        </h2>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="https://growingfamily.co.uk/garden-tips/best-low-maintenance-plants-for-outdoor-pots/"
            >
              Low-Maintenance Plants for Outdoor Pots
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://sowsmallgarden.com/benefits-of-gardening/"
            >
              12 Benefits of Gardening
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://extension.umn.edu/planting-and-growing-guides/growing-healthy-vegetables"
            >
              Growing Healthy Vegetables
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.gardeningknowhow.com/garden-how-to/info/spring-gardening-for-beginners.htm"
            >
              Spring Gardening for Beginners
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://commonsensehome.com/start-a-garden/"
            >
              How to Start a Garden
            </a>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>
          Vegetable and Flower Gardening
        </h2>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="https://www.familyhandyman.com/article/how-to-grow-a-rose-bush/"
            >
              How to Grow a Rose Bush
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://naldc.nal.usda.gov/download/CAT87209981/PDF"
            >
              Growing Your Own Vegetables
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://extension.illinois.edu/blogs/garden-scoop/2023-03-10-spring-flowering-trees-and-shrubs"
            >
              Spring Flowering Trees and Shrubs
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.almanac.com/unpacking-and-dividing-stored-dahlias"
            >
              Handling Dahlia Tubers
            </a>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>Garden Design</h2>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="http://www.gardening.cornell.edu/homegardening/scene74a6.html"
            >
              Flower Garden Design Basics
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.bhg.com/gardening/landscaping-projects/solving-shade/shade-garden-design-ideas/"
            >
              20 Shade Garden Design Ideas
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.homesandgardens.com/gardens/what-are-raised-garden-beds-good-for"
            >
              What Are Raised Garden Beds Good For?
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.gardenersworld.com/how-to/grow-plants/nine-garden-design-tips/"
            >
              Nine Garden Design Tips
            </a>
          </li>
          <li>
            <a href="https://www.realestateagents.com/ca/garden-grove">
              Garden Grove
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.thespruce.com/free-garden-planners-1357749"
            >
              Top Garden Planners and Programs
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://gardenandhappy.com/free-garden-planners/"
            >
              Ten of the Best Free Garden Planners Available Online
            </a>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>Gardening for Kids</h2>
        <ul>
          <li>
            <a rel="nofollow" href="https://www.fao.org/3/i1689e/i1689e00.pdf">
              How and Why to Create School Gardens
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://kidsgardening.org/resources/garden-activities-kitchen-scrap-gardening/"
            >
              Kitchen Scrap Gardening
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.betterhealth.vic.gov.au/health/healthyliving/gardening-for-children"
            >
              Tips for Gardening With Children
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.kcedventures.com/gardening-with-kids-measure-plant-growth/"
            >
              Measuring Plant Growth
            </a>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>
          Organizations/Societies
        </h2>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="https://ahsgardening.org/ways-to-support/membership/"
            >
              American Horticultural Society
            </a>
          </li>
          <li>
            <a rel="nofollow" href="https://www.rhs.org.uk/join">
              Royal Horticultural Society
            </a>
          </li>
          <li>
            <a rel="nofollow" href="https://garden.org/">
              National Gardening Association
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.gardenorganic.org.uk/what-we-do"
            >
              Garden Organic
            </a>
          </li>
          <li>
            <a rel="nofollow" href="https://www.gardenclinic.com.au/about-us">
              The Garden Clinic
            </a>
          </li>
          <li>
            <a rel="nofollow" href="https://www.gcamerica.org/about">
              The Garden Club of America
            </a>
          </li>
          <li>
            <a rel="nofollow" href="https://www.herbsociety.org/get-involved/">
              The Herb Society of America
            </a>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>Composting</h2>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="https://www.epa.gov/recycle/composting-home"
            >
              Composting at Home
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.oregonmetro.gov/sites/default/files/2014/10/29/2010_home_composting_booklet.pdf"
            >
              It's Easy to Make Your Own Compost
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://aggie-horticulture.tamu.edu/wp-content/uploads/sites/10/2013/09/EHT-069.pdf"
            >
              Easy Garden Composting
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://scdhec.gov/sites/default/files/Library/OR-1705.pdf"
            >
              Composting: Recycling Naturally
            </a>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>Periodicals</h2>
        <ul>
          <li>
            <a rel="nofollow" href="https://www.gardendesign.com/">
              <em>Garden Design</em> Magazine
            </a>
          </li>
          <li>
            <a rel="nofollow" href="https://www.gardensillustrated.com/">
              <em>Gardens Illustrated</em>
            </a>
          </li>
          <li>
            <a rel="nofollow" href="https://www.gardengatemagazine.com/">
              <em>Garden Gate</em> Magazine
            </a>
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>Podcasts</h2>
        <ul>
          <li>
            <a rel="nofollow" href="https://joegardener.com/podcasts/">
              The Joe Gardener Show
            </a>
          </li>
          <li>
            <a rel="nofollow" href="https://www.finegardening.com/podcast">
              Let's Argue About Plants
            </a>
          </li>
          <li>
            <a rel="nofollow" href="https://www.epicgardening.com/podcast/">
              Epic Gardening Podcast
            </a>
          </li>
          <li>
            <a rel="nofollow" href="https://www.janeperrone.com/on-the-ledge">
              On the Ledge
            </a>
          </li>
          <li>
            <a href="https://www.realestateagents.com/id/garden-valley">
              Garden Valley
            </a>
          </li>
        </ul>
      </>
    );
  };

  return (
    <>
      <Head
        title={
          "A Homeowner's Resource Guide to Gardening Around Your Property | RealEstateAgents.com"
        }
        description={
          "Resources to help you and your family develop a home garden."
        }
      />
      <article className={styles["Article"]}>
        <div className={styles["Article__container"]}>{content()}</div>
      </article>
      <Script strategy="beforeInteractive" type="application/ld+json">
        {{
          "@context": "https://schema.org",
          "@type": "Article",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id":
              "https://www.realestateagents.com/articles/homeowners-resource-guide-to-gardening",
          },
          headline:
            "A Homeowner's Resource Guide to Gardening Around Your Property",
          author: {
            "@type": "Person",
            name: "Amber Martinez",
            jobTitle: "Education Writer",
            sameAs: [
              "https://www.realestateagents.com/articles/author/amber-martinez",
              "https://www.linkedin.com/in/amber-martinez-7096651a/",
            ],
          },
        }}
      </Script>
    </>
  );
}
